import {
    ApiOutlined,
    AppstoreAddOutlined,
    BankOutlined,
    BarcodeOutlined,
    BellOutlined,
    CalendarOutlined,
    CarFilled,
    CarOutlined,
    CheckSquareOutlined,
    ControlOutlined,
    DashboardOutlined,
    DollarCircleOutlined,
    FileProtectOutlined,
    FileTextOutlined,
    GlobalOutlined,
    LayoutOutlined,
    PropertySafetyOutlined,
    SafetyCertificateOutlined,
    SettingOutlined,
    ShopOutlined,
    ShoppingCartOutlined,
    SmileOutlined,
    SoundOutlined,
    TagOutlined,
    TeamOutlined,
    ToolOutlined,
    TruckOutlined,
} from '@ant-design/icons';
import type { Route } from '@ant-design/pro-layout/lib/typings';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as permissionKind from '../../../shared/permissions';
import { useAccount } from '../../components/contexts/AccountContextManager';
import { useCompany, useCompanyContext } from '../../components/contexts/CompanyContextManager';
import hasPermissions from '../../utilities/hasPermissions';

const useConsoleMenu = (isCollapsed?: boolean) => {
    const { t } = useTranslation('consoleLayout');
    const { permissions: accountPermissions } = useAccount();

    const { companies } = useCompanyContext();

    const selectedCompany = useCompany(true);

    return useMemo((): Route => {
        const hasCompanies = companies.length > 0;

        const hasModuleManagement = hasCompanies
            ? companies.some(
                  company =>
                      hasPermissions(company.permissions, [permissionKind.createModule]) ||
                      hasPermissions(company.permissions, [permissionKind.updateModule])
              )
            : hasPermissions(accountPermissions, [permissionKind.createModule]);

        const hasRouterManagement = hasCompanies
            ? companies.some(company => hasPermissions(company.permissions, [permissionKind.viewRouters]))
            : hasPermissions(accountPermissions, [permissionKind.viewRouters]);

        const systemRoutes: Route[] = [
            hasModuleManagement && {
                name: t('consoleLayout:menuItems.modules'),
                path: '/admin/system/modules',
                key: 'modules',
            },
            hasRouterManagement && {
                name: t('consoleLayout:menuItems.routers'),
                path: '/admin/system/routers',
                key: 'routers',
            },
            hasPermissions(accountPermissions, [permissionKind.viewLanguagePacks]) && {
                name: t('consoleLayout:menuItems.languages'),
                path: '/admin/system/languages',
                key: 'languages',
            },
        ].filter(Boolean);

        const hasUserManagement = hasCompanies
            ? companies.some(company => hasPermissions(company.permissions, [permissionKind.viewUsers]))
            : hasPermissions(accountPermissions, [permissionKind.viewUsers]);

        const hasRolesManagement = hasCompanies
            ? companies.some(company => hasPermissions(company.permissions, [permissionKind.viewRoles]))
            : hasPermissions(accountPermissions, [permissionKind.viewRoles]);

        const hasUserGroupManagement = hasCompanies
            ? companies.some(company => hasPermissions(company.permissions, [permissionKind.viewUserGroups]))
            : hasPermissions(accountPermissions, [permissionKind.viewUserGroups]);

        const hasDealerManagement = companies.some(company =>
            hasPermissions(company.permissions, [permissionKind.viewDealers])
        );

        const accessRoutes: Route[] = [
            hasUserManagement && {
                name: t('consoleLayout:menuItems.users'),
                path: '/admin/accesses/users',
                key: 'users',
            },
            hasRolesManagement && {
                name: t('consoleLayout:menuItems.roles'),
                path: '/admin/accesses/roles',
                key: 'roles',
            },
            hasUserGroupManagement && {
                name: t('consoleLayout:menuItems.userGroups'),
                path: '/admin/accesses/userGroups',
                key: 'userGroups',
            },
        ].filter(Boolean);

        const hasVehicleManagement = companies.some(
            company =>
                company.availableModules.hasVehicleManagementModules &&
                hasPermissions(company.permissions, [permissionKind.viewVehicles])
        );

        const hasFinderVehicleManagement = companies.some(
            company =>
                company.availableModules.hasFinderVehicleManagementModules &&
                hasPermissions(company.permissions, [permissionKind.viewFinderVehicles])
        );

        const hasFinancing = companies.some(
            company =>
                company.availableModules.hasApplicationModules &&
                company.availableModules.hasBanks &&
                hasPermissions(company.permissions, [permissionKind.viewFinanceApplications])
        );

        const hasReservation = companies.some(
            company =>
                company.availableModules.hasApplicationModules &&
                company.availableModules.hasPaymentModules &&
                hasPermissions(company.permissions, [permissionKind.viewReservations])
        );
        const hasLeads = companies.some(
            company =>
                company.availableModules.hasApplicationModules &&
                hasPermissions(company.permissions, [permissionKind.viewLeads])
        );

        const hasInsurance = companies.some(
            company =>
                company.availableModules.hasApplicationModules &&
                company.availableModules.hasInsuranceModules &&
                hasPermissions(company.permissions, [permissionKind.viewInsuranceApplications])
        );

        const hasAppointments = companies.some(
            company =>
                company.availableModules.hasAppointmentModules &&
                hasPermissions(company.permissions, [permissionKind.viewAppointments])
        );

        // This one is only for mobility booking submission
        const hasBookings = companies.some(
            company =>
                company.availableModules.hasMobilityModules &&
                hasPermissions(company.permissions, [permissionKind.viewBookingApplications])
        );

        const accessConfigurators = companies.some(
            company =>
                company.availableModules.hasConfiguratorModules &&
                hasPermissions(company.permissions, [permissionKind.viewConfigurators])
        );

        const accessEvents = companies.some(
            company =>
                company.availableModules.hasEventModules &&
                hasPermissions(company.permissions, [permissionKind.viewEvents])
        );

        const accessPromotions = companies.some(
            company =>
                company.availableModules.hasPromotionCodeModules &&
                hasPermissions(company.permissions, [permissionKind.viewPromoCodes])
        );

        const accessInventories = companies.some(
            company =>
                company.availableModules.hasInventories &&
                hasPermissions(company.permissions, [permissionKind.viewInventories])
        );

        const accessMaintenances = companies.some(
            company =>
                company.activeMaintenanceModule &&
                hasPermissions(company.permissions, [permissionKind.manageMaintenance])
        );

        const accessInsurers = companies.some(
            company =>
                company.availableModules.hasInsuranceModules &&
                hasPermissions(company.permissions, [permissionKind.manageInsurers])
        );

        const accessGiftVoucher = companies.some(
            company =>
                company.availableModules.hasGiftVoucherModules &&
                hasPermissions(company.permissions, [permissionKind.viewGiftVouchers])
        );

        const hasTradeIn = companies.some(
            company =>
                company.availableModules.hasTradeInModules &&
                hasPermissions(company.permissions, [permissionKind.viewTradeIns])
        );

        const accessAgreements = companies.some(
            company =>
                company.availableModules.hasAgreementModules &&
                hasPermissions(company.permissions, [permissionKind.viewAgreements])
        );

        const accessBanks = companies.some(
            company =>
                company.availableModules.hasSystemBanks &&
                hasPermissions(company.permissions, [permissionKind.manageBanks])
        );

        const accessFinanceProducts = companies.some(
            company =>
                company.availableModules.hasFinanceProducts &&
                hasPermissions(company.permissions, [permissionKind.viewFinanceProducts])
        );

        const accessInsuranceProduct = companies.some(
            company =>
                company.availableModules.hasInsuranceModules &&
                hasPermissions(company.permissions, [permissionKind.viewInsuranceProducts])
        );

        const accessLabels = companies.some(
            company =>
                company.availableModules.hasLabelsModules &&
                hasPermissions(company.permissions, [permissionKind.viewLabels])
        );

        const accessMobility = companies.some(
            company =>
                company.availableModules.hasMobilityModules &&
                hasPermissions(company.permissions, [permissionKind.viewMobilities])
        );

        const accessWebpages = companies.some(
            company =>
                company.availableModules.hasWebsiteModules &&
                hasPermissions(company.permissions, [permissionKind.viewWebpages])
        );

        const viewDashboard = companies.some(company =>
            hasPermissions(company.permissions, [permissionKind.viewDashboard])
        );

        const hasCustomerModules = companies.some(
            company =>
                company.availableModules.hasCustomerModules &&
                hasPermissions(company.permissions, [permissionKind.viewCustomers])
        );

        const accessCts = companies.some(
            company =>
                company.availableModules.hasCtsModules &&
                hasPermissions(company.permissions, [permissionKind.manageCts])
        );

        const accessAudiCalculation = companies.some(
            company =>
                selectedCompany &&
                company.availableModules.hasAudiCalculationModules &&
                hasPermissions(company.permissions, [permissionKind.viewAudiCalculation])
        );

        const miscMenus = [
            viewDashboard && {
                name: t('consoleLayout:menuItems.dashboard'),
                path: '/admin/dashboard',
                key: 'overview',
                icon: <DashboardOutlined />,
            },
        ].filter(Boolean);

        const applicationMenus = [
            {
                name: t('consoleLayout:console.applications'),
                path: '/',
                key: 'console-applications',
                isSubMenu: true,
                // replicate icon style
                ...(isCollapsed && {
                    icon: (
                        <span className="anticon" style={{ transition: 'none' }}>
                            {t('consoleLayout:console.applications')?.[0]}
                        </span>
                    ),
                }),
            },
            hasFinancing && {
                name: t('consoleLayout:menuItems.applications'),
                path: '/admin/applications',
                key: 'applications',
                icon: <FileTextOutlined />,
            },
            hasReservation && {
                name: t('consoleLayout:menuItems.reservations'),
                path: '/admin/reservations',
                key: 'reservations',
                icon: <ShoppingCartOutlined />,
            },
            hasInsurance && {
                name: t('consoleLayout:menuItems.insurances'),
                path: '/admin/insurances',
                key: 'insurances',
                icon: <FileProtectOutlined />,
            },
            hasLeads && {
                name: t('consoleLayout:menuItems.leads'),
                path: '/admin/leads',
                key: 'leads',
                icon: <SoundOutlined />,
            },
            hasAppointments && {
                name: t('consoleLayout:menuItems.appointments'),
                path: '/admin/appointments',
                key: 'appointments',
                icon: <BellOutlined />,
            },
            hasBookings && {
                name: t('consoleLayout:menuItems.mobilitybookings'),
                path: '/admin/mobilitybookings',
                key: 'mobilitybookings',
                icon: <CalendarOutlined />,
            },
            hasTradeIn && {
                name: t('consoleLayout:menuItems.tradeIns'),
                path: '/admin/tradeIns',
                key: 'tradeIns',
                icon: <TruckOutlined />,
            },
            hasCustomerModules && {
                name: t('consoleLayout:menuItems.customers'),
                path: '/admin/customers',
                key: 'customers',
                icon: <SmileOutlined />,
            },
            accessGiftVoucher && {
                name: t('consoleLayout:menuItems.giftVouchers'),
                path: '/admin/giftCodes',
                key: 'giftVouchers',
                icon: <TagOutlined />,
            },
        ].filter(Boolean);

        const contentMenus = [
            {
                name: t('consoleLayout:console.contents'),
                path: '/',
                key: 'contents',
                isSubMenu: true, // replicate icon style
                ...(isCollapsed && {
                    icon: (
                        <span className="anticon" style={{ transition: 'none' }}>
                            {t('consoleLayout:console.contents')?.[0]}
                        </span>
                    ),
                }),
            },
            accessPromotions && {
                name: t('consoleLayout:menuItems.promoCodes'),
                path: '/admin/promoCodes',
                key: 'promoCodes',
                icon: <DollarCircleOutlined />,
            },
            accessLabels && {
                name: t('consoleLayout:menuItems.labels'),
                path: '/admin/labels',
                key: 'labels',
                icon: <TagOutlined />,
            },
            accessEvents && {
                name: t('consoleLayout:menuItems.events'),
                path: '/admin/events',
                key: 'events',
                icon: <CalendarOutlined />,
            },
            accessConfigurators && {
                name: t('consoleLayout:menuItems.configurators'),
                path: '/admin/configurators',
                key: 'configuratorsMenu',
                icon: <ControlOutlined />,
                routes: [
                    {
                        name: t('consoleLayout:menuItems.configurators'),
                        path: '/admin/configurators',
                        key: 'configurators',
                    },
                    {
                        name: t('consoleLayout:menuItems.banners'),
                        path: '/admin/banners',
                        key: 'banners',
                    },
                ],
            },
            accessWebpages && {
                name: t('consoleLayout:menuItems.webpages'),
                path: '/admin/webpages',
                key: 'webpages',
                icon: <LayoutOutlined />,
            },
            accessMobility && {
                name: t('consoleLayout:menuItems.mobilities'),
                path: '/admin/mobilityoptions',
                key: 'mobilities',
                icon: <AppstoreAddOutlined />,
            },
            hasVehicleManagement && {
                name: t('consoleLayout:menuItems.vehicles'),
                key: 'vehicles',
                path: '/admin/vehicles',
                icon: <CarOutlined />,
                routes: [
                    {
                        name: t('consoleLayout:menuItems.makes'),
                        path: '/admin/vehicles/makes',
                        key: 'makes',
                    },
                    {
                        name: t('consoleLayout:menuItems.models'),
                        path: '/admin/vehicles/models',
                        key: 'models',
                    },
                    {
                        name: t('consoleLayout:menuItems.subModels'),
                        path: '/admin/vehicles/subModels',
                        key: 'subModels',
                    },
                    {
                        name: t('consoleLayout:menuItems.variants'),
                        path: '/admin/vehicles/variants',
                        key: 'variants',
                    },
                ],
            },
            accessInventories && {
                name: t('consoleLayout:menuItems.inventory'),
                path: '/admin/inventories',
                key: 'inventories',
                icon: <BarcodeOutlined />,
            },
            hasFinderVehicleManagement && {
                name: t('consoleLayout:menuItems.finderVehicles'),
                key: 'finderVehicles',
                path: '/admin/finderVehicles',
                icon: <CarFilled />,
            },
            accessFinanceProducts && {
                name: t('consoleLayout:menuItems.financeProducts'),
                path: '/admin/financeProducts',
                key: 'financeProducts',
                icon: <DollarCircleOutlined />,
            },
            accessInsuranceProduct && {
                name: t('consoleLayout:menuItems.insuranceProducts'),
                path: '/admin/insuranceProducts',
                key: 'insuranceProducts',
                icon: <PropertySafetyOutlined />,
            },
            accessCts && {
                name: t('consoleLayout:menuItems.cts'),
                path: '/admin/cts',
                key: 'cts',
                icon: <ApiOutlined />,
            },
            accessAudiCalculation && {
                name: t('consoleLayout:menuItems.audiCalculation'),
                path: '/admin/audiCalculation',
                key: 'audiCalculation',
                icon: <ApiOutlined />,
            },
            accessAgreements && {
                name: t('consoleLayout:menuItems.consents'),
                path: '/admin/consents',
                key: 'consents',
                icon: <CheckSquareOutlined />,
            },
            accessMaintenances && {
                name: t('consoleLayout:menuItems.maintenance'),
                path: '/admin/maintenance',
                key: 'maintenance',
                icon: <ToolOutlined />,
            },
        ].filter(Boolean);

        const settingMenu = [
            {
                name: t('consoleLayout:console.settings'),
                path: '/',
                key: 'settings',
                isSubMenu: true,
                ...(isCollapsed && {
                    icon: (
                        <span className="anticon" style={{ transition: 'none' }}>
                            {t('consoleLayout:console.settings')?.[0]}
                        </span>
                    ),
                }),
            },
            accessRoutes.length > 0 && {
                name: t('consoleLayout:menuItems.usersandroles'),
                key: 'userandrole',
                path: '/admin/accesses',
                icon: <TeamOutlined />,
                routes: accessRoutes,
            },
            hasDealerManagement && {
                name: t('consoleLayout:menuItems.dealers'),
                path: '/admin/dealers',
                key: 'dealers',
                icon: <ShopOutlined />,
            },
            hasPermissions(accountPermissions, [permissionKind.viewCompanies]) && {
                name: t('consoleLayout:menuItems.companyandcountry'),
                path: '/admin/companies',
                key: 'companies',
                icon: <GlobalOutlined />,
            },
            accessBanks && {
                name: t('consoleLayout:menuItems.banks'),
                path: '/admin/banks',
                key: 'banks',
                icon: <BankOutlined />,
            },
            accessInsurers && {
                name: t('consoleLayout:menuItems.insurers'),
                path: '/admin/insurers',
                key: 'insurers',
                icon: <SafetyCertificateOutlined />,
            },
            systemRoutes.length > 0 && {
                name: t('consoleLayout:menuItems.system'),
                key: 'system',
                path: '/admin/system',
                icon: <SettingOutlined />,
                routes: systemRoutes,
            },
        ].filter(Boolean);

        const mapIsLast = arrayLength => (menu, index) => {
            if (index !== arrayLength - 1) {
                return menu;
            }

            return { ...menu, isLast: true };
        };

        // if do not have sub menu under section, will hidden whole section
        const mapMenu = array => (array.length <= 1 ? [] : array);

        return {
            routes: [
                ...miscMenus.map(mapIsLast(miscMenus.length)),
                ...mapMenu(applicationMenus).map(mapIsLast(applicationMenus.length)),
                ...mapMenu(contentMenus).map(mapIsLast(contentMenus.length)),
                ...mapMenu(settingMenu),
            ].filter(Boolean),
        };
    }, [companies, accountPermissions, t, isCollapsed]);
};

export default useConsoleMenu;
